import cx from 'classnames';
import type { ComponentChildren, FunctionComponent, ComponentProps } from 'preact';
import { useRef } from 'preact/hooks';
import { FocusOn as _FocusOn } from 'react-focus-on';
import styles from './Dialog.module.css';
import { useDraggable } from '../../hooks/useDraggable.js';
import { Button } from '../Button/Button.js';
import { elementIds } from '../../constants.js';
import { Dismiss16 } from '../icons/Dismiss16.js';

// Re-type children for preact
const FocusOn = _FocusOn as FunctionComponent<
  Omit<ComponentProps<typeof _FocusOn>, 'children'> & { children: ComponentChildren }
>;

export interface DialogProps {
  title: string;
  id: string;
  style?: 'default' | 'error';
  draggable?: boolean;
  onClose?: () => void;
  commands?: ComponentChildren;
  children: ComponentChildren;
}

/**
 * Dialog component which takes title and commands as props and content as children.
 *
 * If multiple children are provided, they'll be styled with a flex column layout with gaps.
 * To prevent this behavior, wrap the children in a single div.
 */
export const Dialog: FunctionComponent<DialogProps> = (props) => {
  const { title, id, style, draggable, onClose, commands, children } = props;
  const dialogRootRef = useRef<HTMLDivElement>(null);
  const titleRef = useRef<HTMLDivElement>(null);

  useDraggable({
    enabled: !!draggable,
    containerElementRef: dialogRootRef,
    dragElementRef: titleRef,
  });

  return (
    // The overlay must be outside FocusOn so that clicking on it will close the dialog
    <div className={styles.overlay}>
      <FocusOn
        className={styles.focusOn}
        onClickOutside={onClose}
        onEscapeKey={onClose}
        onActivation={() => titleRef.current?.focus()}
      >
        <div
          id={id}
          ref={dialogRootRef}
          class={cx(styles.dialog, style === 'error' && styles.error)}
          role="dialog"
          aria-modal
          aria-labelledby={elementIds.dialogTitle}
        >
          <div class={styles.titleArea}>
            <h2
              ref={titleRef}
              id={elementIds.dialogTitle}
              class={cx(styles.title, draggable && styles.draggable)}
              tabIndex={-1}
            >
              {title}
            </h2>
            {onClose && (
              // a close icon is common/universal enough that it doesn't need hover text
              <Button variant="iconOnly" onClick={onClose} aria-label="Close">
                <Dismiss16 />
              </Button>
            )}
          </div>
          {commands && <div class={styles.commands}>{commands}</div>}
          <div class={styles.content}>{children}</div>
        </div>
      </FocusOn>
    </div>
  );
};
